import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "../cn";

export interface SelectOption {
    value: string;
    label: string;
    badge?: string;
}

export interface SelectProps {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    /** Align the dropdown to the right edge of the trigger */
    alignRight?: boolean;
    /** Minimum dropdown width in px */
    minWidth?: number;
    id?: string;
}

/* 1:1 port of discordforge.org src/components/ui/CustomSelect.tsx — same
 * trigger, portal dropdown, positioning and option styling. */
export function Select({
    value,
    onChange,
    options,
    placeholder = "Select an option",
    disabled = false,
    className = "",
    alignRight = false,
    minWidth = 220,
    id,
}: SelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [coords, setCoords] = useState<{ top: number; left: number; width: number }>({
        top: 0,
        left: 0,
        width: 0,
    });
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
    }, []);

    const updateCoords = () => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const calcWidth = Math.max(rect.width, minWidth);
            let calculatedLeft = rect.left + window.scrollX;

            const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1200;
            const overflowsRight = rect.left + calcWidth > viewportWidth - 16;

            if (alignRight || overflowsRight) {
                calculatedLeft = Math.max(16 + window.scrollX, rect.right + window.scrollX - calcWidth);
            }

            setCoords({
                top: rect.bottom + window.scrollY + 6,
                left: calculatedLeft,
                width: calcWidth,
            });
        }
    };

    const handleToggle = () => {
        if (disabled) return;
        if (!isOpen) {
            updateCoords();
        }
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        if (!isOpen) return;

        function handleClickOutside(event: MouseEvent) {
            const target = event.target as Node;
            if (
                buttonRef.current &&
                !buttonRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false);
            }
        }

        function handleScrollOrResize() {
            updateCoords();
        }

        document.addEventListener("mousedown", handleClickOutside);
        window.addEventListener("scroll", handleScrollOrResize, true);
        window.addEventListener("resize", handleScrollOrResize);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            window.removeEventListener("scroll", handleScrollOrResize, true);
            window.removeEventListener("resize", handleScrollOrResize);
        };
    }, [isOpen]);

    const selectedOption = options.find((o) => o.value === value);

    const dropdownContent = (
        <div
            ref={dropdownRef}
            style={{
                position: "absolute",
                top: `${coords.top}px`,
                left: `${coords.left}px`,
                width: `${coords.width}px`,
                zIndex: 999999,
            }}
            className="overflow-hidden rounded-2xl border border-white/[0.15] bg-zinc-950/95 p-1.5 shadow-[0_25px_60px_rgba(0,0,0,0.95)] backdrop-blur-2xl animate-df-zoom-in"
        >
            <div className="max-h-60 space-y-1 overflow-y-auto custom-scrollbar">
                {options.map((opt) => {
                    const isSelected = opt.value === value;
                    return (
                        <button
                            key={opt.value}
                            type="button"
                            onClick={() => {
                                onChange(opt.value);
                                setIsOpen(false);
                            }}
                            className={cn(
                                "flex w-full cursor-pointer items-center justify-between gap-2 rounded-xl px-3.5 py-2.5 text-left text-xs transition-all sm:text-sm",
                                isSelected
                                    ? "border border-orange-500/30 bg-orange-500/20 font-bold text-orange-400"
                                    : "border border-transparent text-zinc-300 hover:bg-white/10 hover:text-white"
                            )}
                        >
                            <div className="flex min-w-0 items-center gap-2 truncate">
                                <span className="truncate">{opt.label}</span>
                                {opt.badge && (
                                    <span className="shrink-0 rounded-md border border-orange-500/30 bg-orange-500/15 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-orange-400">
                                        {opt.badge}
                                    </span>
                                )}
                            </div>
                            {isSelected && <Check size={14} className="shrink-0 text-orange-400" />}
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className={cn("relative min-w-0", className)}>
            <button
                ref={buttonRef}
                id={id}
                type="button"
                onClick={handleToggle}
                disabled={disabled}
                className="flex w-full cursor-pointer items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/20 px-3.5 py-2.5 text-left text-[14px] font-medium text-white outline-none transition focus:border-orange-400/60 focus:ring-2 focus:ring-orange-400/15 disabled:cursor-not-allowed disabled:opacity-50"
            >
                <div className="flex min-w-0 items-center gap-2 truncate">
                    <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
                    {selectedOption?.badge && (
                        <span className="shrink-0 rounded-md border border-orange-500/30 bg-orange-500/15 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-orange-400">
                            {selectedOption.badge}
                        </span>
                    )}
                </div>
                <ChevronDown
                    size={16}
                    className={cn(
                        "shrink-0 text-zinc-400 transition-transform duration-200",
                        isOpen && "rotate-180 text-orange-400"
                    )}
                />
            </button>

            {isOpen && mounted && createPortal(dropdownContent, document.body)}
        </div>
    );
}
